import React, { useState, useEffect } from 'react';
import { 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { httpsCallable, Functions } from 'firebase/functions';
import '../../styles/CollectionView.css';

interface CollectionField {
  id: string;
  name: string;
  lastUpdated: number;
  enabled?: boolean;
  permissions?: Record<string, string | string[]>;
}

interface SchemaField {
  key: string;
  label: string;
  type: 'freeform' | 'enum';
  required: boolean;
  defaultValue?: string;
  options?: string[];
}

interface CustomField {
  key: string;
  label: string;
  value: any;
}

interface CollectionMetadata {
  id: string;
  permissions: Record<string, string | string[]>;
  fields: CollectionField[];
  lastUpdated?: number;
  schema?: SchemaField[];
}

interface DocumentData {
  id: string;
  name: string;
  data: Record<string, any>;
  lastUpdated: number;
}

interface CollectionViewProps {
  collectionId: string;
  instanceAlias: string; // e.g., "Conference", "User", "Event"
  db: Firestore;
  functions: Functions;
  onEdit?: (itemId: string) => void; // Optional edit handler
  setError: (error: string) => void;
  setSuccess: (success: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const CollectionView: React.FC<CollectionViewProps> = ({
  collectionId,
  instanceAlias,
  db,
  functions,
  onEdit,
  setError,
  setSuccess,
  loading,
  setLoading
}) => {
  const [collectionData, setCollectionData] = useState<CollectionMetadata | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingItem, setEditingItem] = useState<CollectionField | null>(null);
  const [editingDocument, setEditingDocument] = useState<DocumentData | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [newCustomField, setNewCustomField] = useState<{ key: string; label: string }>({ key: '', label: '' });

  // Generate unique document ID with hex suffix
  const generateUniqueDocId = (baseName: string): string => {
    // Generate base ID from name
    const baseId = baseName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    
    // Always append 6-character hex suffix for uniqueness
    const hexSuffix = Math.random().toString(16).substring(2, 8);
    return `${baseId}-${hexSuffix}`;
  };

  // Load collection metadata and fields
  const loadCollection = async () => {
    try {
      setLoading(true);
      const metadataRef = doc(db, 'metadata', collectionId);
      const metadataSnapshot = await getDoc(metadataRef);
      
      if (metadataSnapshot.exists()) {
        setCollectionData({
          id: collectionId,
          ...metadataSnapshot.data()
        } as CollectionMetadata);
      } else {
        setError(`Collection '${collectionId}' not found`);
      }
    } catch (error) {
      console.error('Error loading collection:', error);
      setError('Failed to load collection data');
    } finally {
      setLoading(false);
    }
  };

  // Load document data for editing
  const loadDocumentData = async (itemId: string) => {
    try {
      setLoading(true);
      const docRef = doc(db, collectionId, itemId);
      const docSnapshot = await getDoc(docRef);
      
      if (docSnapshot.exists()) {
        const docData = docSnapshot.data() as DocumentData;
        setEditingDocument(docData);
        
        // Initialize form data with existing document data
        const existingData = docData.data || {};
        setFormData(existingData);
        
        // Extract custom fields (fields not in schema)
        const schemaKeys = collectionData?.schema?.map(field => field.key) || [];
        const systemKeys = ['name', 'created', 'enabled', 'lastUpdated', 'id'];
        const allKnownKeys = [...schemaKeys, ...systemKeys];
        
        const customFieldsFromData = Object.entries(existingData)
          .filter(([key]) => !allKnownKeys.includes(key))
          .map(([key, value]) => ({
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1),
            value
          }));
        
        setCustomFields(customFieldsFromData);
      } else {
        setError('Document not found');
      }
    } catch (error) {
      console.error('Error loading document:', error);
      setError('Failed to load document data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollection();
  }, [collectionId, db]);

  // Initialize form data with schema defaults
  const initializeFormData = () => {
    const initialData: Record<string, any> = {
      enabled: true // Default enabled state
    };
    
    if (collectionData?.schema) {
      collectionData.schema.forEach(field => {
        if (field.defaultValue) {
          initialData[field.key] = field.defaultValue;
        } else if (field.type === 'enum' && field.options && field.options.length > 0) {
          initialData[field.key] = field.options[0];
        } else {
          initialData[field.key] = '';
        }
      });
    }
    
    setFormData(initialData);
    setCustomFields([]);
  };

  // Handle form field changes
  const handleFormFieldChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Handle custom field changes
  const handleCustomFieldChange = (index: number, property: 'key' | 'value', value: any) => {
    setCustomFields(prev => {
      const updated = [...prev];
      if (property === 'key') {
        // Check for forbidden keys
        const forbiddenKeys = ['id', 'name'];
        if (forbiddenKeys.includes(value.toLowerCase())) {
          setError(`Key "${value}" is not allowed. Reserved keys: ${forbiddenKeys.join(', ')}`);
          return prev; // Don't update if forbidden key
        }
        updated[index] = { ...updated[index], key: value, label: value };
      } else {
        updated[index] = { ...updated[index], [property]: value };
      }
      return updated;
    });
  };

  // Add new custom field
  const addCustomField = () => {
    // This function is now simplified since we add fields directly via button
    setCustomFields(prev => [...prev, { key: '', label: '', value: '' }]);
  };

  // Remove custom field
  const removeCustomField = (index: number) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  // Validate form data against schema (only for editing, not initial creation)
  const validateFormData = (isCreating: boolean = false): boolean => {
    // Skip validation for initial creation - only name is required
    if (isCreating || !collectionData?.schema) return true;

    for (const field of collectionData.schema) {
      if (field.required && (!formData[field.key] || formData[field.key].toString().trim() === '')) {
        setError(`${field.label} is required`);
        return false;
      }
    }
    return true;
  };

  // Add new item to collection
  const handleAddItem = async () => {
    if (!newItemName.trim()) {
      setError(`${instanceAlias} name is required`);
      return;
    }

    // Skip field validation for initial creation
    if (!validateFormData(true)) {
      return;
    }

    try {
      setLoading(true);
      const createDocument = httpsCallable(functions, 'createDocument');
      
      // Generate unique document ID with hex suffix
      const docId = generateUniqueDocId(newItemName.trim());
      
      console.log('Generated docId:', docId); // Debug log
      console.log('Original name:', newItemName.trim()); // Debug log
      
      // For initial creation, only include basic required fields
      const documentData = {
        name: newItemName.trim(),
        created: Date.now(),
        enabled: true, // Default to enabled
      };

      await createDocument({
        collectionName: collectionId,
        docId: docId,
        docName: newItemName.trim(),
        data: documentData
      });

      setSuccess(`${instanceAlias} '${newItemName}' created successfully`);
      setNewItemName('');
      setFormData({});
      setCustomFields([]);
      setShowAddForm(false);
      await loadCollection(); // Refresh the list
    } catch (error: any) {
      console.error('Error creating item:', error);
      setError(error.message || `Failed to create ${instanceAlias.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  // Update existing item
  const handleUpdateItem = async () => {
    if (!editingItem || !editingDocument) {
      setError('No item selected for editing');
      return;
    }

    if (!validateFormData()) {
      return;
    }

    try {
      setLoading(true);
      const updateDocument = httpsCallable(functions, 'updateDocument');
      
      // Combine custom fields data (filter out empty keys and forbidden keys)
      const forbiddenKeys = ['id', 'name'];
      const customFieldsData = customFields
        .filter(field => field.key.trim() !== '' && !forbiddenKeys.includes(field.key.toLowerCase())) // Filter out empty and forbidden keys
        .reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {} as Record<string, any>);

      // Combine existing data with form data and custom fields
      const updatedData = {
        ...editingDocument.data,
        ...formData,
        ...customFieldsData,
        lastUpdated: Date.now()
      };

      await updateDocument({
        collectionName: collectionId,
        docId: editingItem.id,
        data: updatedData
      });

      setSuccess(`${instanceAlias} updated successfully`);
      setEditingItem(null);
      setEditingDocument(null);
      setFormData({});
      setCustomFields([]);
      setShowEditForm(false);
      await loadCollection(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating item:', error);
      setError(error.message || `Failed to update ${instanceAlias.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  // Open edit form
  const handleEditClick = async (item: CollectionField) => {
    setEditingItem(item);
    await loadDocumentData(item.id);
    setShowEditForm(true);
  };

  // Duplicate an existing item
  const handleDuplicate = async (item: CollectionField) => {
    try {
      setLoading(true);
      const createDocument = httpsCallable(functions, 'createDocument');
      
      // Load the original document data first
      const docRef = doc(db, collectionId, item.id);
      const docSnapshot = await getDoc(docRef);
      
      let originalData = {};
      if (docSnapshot.exists()) {
        const docData = docSnapshot.data() as DocumentData;
        originalData = docData.data || {};
      }
      
      const duplicateName = `${item.name} (Copy)`;
      const docId = generateUniqueDocId(duplicateName);
      
      console.log('Duplicate docId:', docId); // Debug log
      
      const documentData = {
        ...originalData,
        name: duplicateName,
        created: Date.now(),
        enabled: true,
        originalId: item.id
      };

      await createDocument({
        collectionName: collectionId,
        docId: docId,
        docName: duplicateName,
        data: documentData
      });

      setSuccess(`${instanceAlias} duplicated successfully`);
      await loadCollection(); // Refresh the list
    } catch (error: any) {
      console.error('Error duplicating item:', error);
      setError(error.message || `Failed to duplicate ${instanceAlias.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  // Render form field based on schema
  const renderFormField = (field: SchemaField) => {
    const value = formData[field.key] || '';

    if (field.type === 'enum' && field.options) {
      return (
        <select
          value={value}
          onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
          className="form-select"
          required={field.required}
        >
          <option value="">Select {field.label}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
        placeholder={field.defaultValue || `Enter ${field.label.toLowerCase()}`}
        className="form-input"
        required={field.required}
      />
    );
  };

  // Get status color and text
  const getStatusInfo = (item: CollectionField) => {
    const isEnabled = item.enabled !== false; // Default to enabled if not specified
    return {
      text: isEnabled ? 'Enabled' : 'Disabled',
      className: isEnabled ? 'status-enabled' : 'status-disabled'
    };
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Full-page edit form
  if (showEditForm && editingItem) {
    return (
      <div className="full-page-form">
        <div className="form-header">
          <div className="form-title">
            <h1>Edit {instanceAlias}: {editingItem.name}</h1>
          </div>
          <div className="form-actions">
            <button
              onClick={handleUpdateItem}
              disabled={loading}
              className="update-item-btn primary-btn"
            >
              Save
            </button>
            <button
              onClick={() => {
                setShowEditForm(false);
                setEditingItem(null);
                setEditingDocument(null);
                setFormData({});
                setCustomFields([]);
              }}
              disabled={loading}
              className="cancel-form-btn secondary-btn"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="form-content">
          <div className="form-section">
            <h3>{instanceAlias} Details</h3>
            
            {/* Read-only Org ID */}
            <div className="form-group">
              <label>{instanceAlias} ID:</label>
              <input
                type="text"
                value={editingItem.id}
                readOnly
                className="form-input readonly"
              />
            </div>

            {/* Name field */}
            <div className="form-group">
              <label>{instanceAlias} Name:</label>
              <input
                type="text"
                value={formData.name || editingDocument?.data?.name || ''}
                onChange={(e) => handleFormFieldChange('name', e.target.value)}
                placeholder="Enter name"
                className="form-input"
              />
            </div>

            {/* Enabled/Disabled Toggle */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.enabled !== false}
                  onChange={(e) => handleFormFieldChange('enabled', e.target.checked)}
                  className="form-checkbox"
                />
                Enabled
              </label>
            </div>

            {/* Schema Fields */}
            {collectionData?.schema && collectionData.schema.length > 0 && (
              <>
                {collectionData.schema.map((field) => (
                  <div key={field.key} className="form-group">
                    <label>
                      {field.label}
                      {field.required && ' *'}:
                    </label>
                    {renderFormField(field)}
                  </div>
                ))}
              </>
            )}

            {/* Custom Fields */}
            {customFields.map((field, index) => (
              <div key={index} className="form-group">
                <label>Custom Field:</label>
                <div className="custom-field-row">
                  <input
                    type="text"
                    value={field.key}
                    onChange={(e) => handleCustomFieldChange(index, 'key', e.target.value)}
                    placeholder="Key"
                    className="form-input custom-field-key"
                  />
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="form-input custom-field-value"
                  />
                  <button
                    type="button"
                    onClick={() => removeCustomField(index)}
                    className="remove-field-btn"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Add Custom Field Button */}
            <div className="form-group">
              <button
                type="button"
                onClick={() => setCustomFields(prev => [...prev, { key: '', label: '', value: '' }])}
                className="add-field-btn"
              >
                + Add Custom Field
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    );
  }

  if (!collectionData) {
    return (
      <div className="collection-view-loading">
        <div className="loading-message">Loading {instanceAlias.toLowerCase()}s...</div>
      </div>
    );
  }

  return (
    <div className="collection-view">
      <div className="collection-view-header">
        <h1>{instanceAlias} Management</h1>
        <button
          onClick={() => {
            initializeFormData();
            setShowAddForm(true);
          }}
          disabled={loading}
          className="add-item-btn"
        >
          + Add {instanceAlias}
        </button>
      </div>

      {showAddForm && (
        <div className="add-form-overlay">
          <div className="add-form">
            <h3>Add New {instanceAlias}</h3>
            
            <div className="form-group">
              <label>{instanceAlias} Name: *</label>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={`Enter ${instanceAlias.toLowerCase()} name`}
                className="item-name-input"
                autoFocus
                required
              />
            </div>

            <div className="form-actions">
              <button
                onClick={handleAddItem}
                disabled={loading || !newItemName.trim()}
                className="create-item-btn"
              >
                Create {instanceAlias}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewItemName('');
                  setFormData({});
                  setCustomFields([]);
                }}
                disabled={loading}
                className="cancel-form-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="collection-items-grid">
        {collectionData?.fields && collectionData.fields.length > 0 ? (
          collectionData.fields.map((item) => {
            const statusInfo = getStatusInfo(item);
            return (
              <div key={item.id} className="collection-item-card">
                <div className="item-header">
                  <h3 className="item-name">{item.name}</h3>
                  <span className={`item-status ${statusInfo.className}`}>
                    {statusInfo.text}
                  </span>
                </div>
                
                <div className="item-meta">
                  <div className="item-id">ID: {item.id}</div>
                  <div className="item-date">
                    Updated: {formatDate(item.lastUpdated)}
                  </div>
                </div>

                <div className="item-actions">
                  <button
                    onClick={() => handleDuplicate(item)}
                    disabled={loading}
                    className="duplicate-btn"
                  >
                    Duplicate
                  </button>
                  <button
                    onClick={() => handleEditClick(item)}
                    disabled={loading}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                  {onEdit && (
                    <button
                      onClick={() => onEdit(item.id)}
                      disabled={loading}
                      className="custom-edit-btn"
                    >
                      Custom Edit
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-collection">
            <div className="empty-message">
              <h3>No {instanceAlias.toLowerCase()}s yet</h3>
              <p>Create your first {instanceAlias.toLowerCase()} to get started.</p>
              <button
                onClick={() => {
                  initializeFormData();
                  setShowAddForm(true);
                }}
                disabled={loading}
                className="add-first-item-btn"
              >
                + Add {instanceAlias}
              </button>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CollectionView;